<div class="pc-card">
  <div class="pc-card__body">
    <h2 class="pc-card__title">{{ 'modules.form.section.parameters' | translate }}</h2>
    <p class="pc-card__subtitle">{{ 'modules.form.section.parametersSubtitle' | translate }}</p>

    <div class="pc-section-label" id="pc-inputs-label">
      {{ 'modules.form.parameters.inputsLabel' | translate }}
    </div>
    <div class="pc-table" role="table" aria-labelledby="pc-inputs-label">
      <div class="pc-table__head" role="row">
        <span class="pc-col pc-col--name" role="columnheader">
          {{ 'modules.form.parameters.colName' | translate }}
        </span>
        <span class="pc-col pc-col--type" role="columnheader">
          {{ 'modules.form.parameters.colType' | translate }}
        </span>
        <span class="pc-col pc-col--required" role="columnheader">
          {{ 'modules.form.parameters.colRequired' | translate }}
        </span>
        <span class="pc-col pc-col--default" role="columnheader">
          {{ 'modules.form.parameters.colDefault' | translate }}
        </span>
        <span class="pc-col pc-col--action" role="columnheader">
          <span class="pc-sr-only">{{ 'modules.form.parameters.colActions' | translate }}</span>
        </span>
      </div>

      @for (param of parameters(); track trackRow($index, param); let i = $index) {
        <div class="pc-row" role="row">
          <span class="pc-col pc-col--name" role="cell">
            <input
              class="pc-input pc-input--name"
              [class.pc-input--invalid]="isNameInvalid(param.name, duplicateParameterNames())"
              [value]="param.name"
              [disabled]="readOnly()"
              [attr.aria-invalid]="isNameInvalid(param.name, duplicateParameterNames())"
              [attr.aria-label]="'modules.form.parameters.nameAria' | translate"
              [placeholder]="'modules.form.parameters.namePlaceholder' | translate"
              (input)="renameParameter(i, $any($event.target).value)"
            />
          </span>
          <span class="pc-col pc-col--type" role="cell">
            <select
              class="pc-select"
              [value]="param.type"
              [disabled]="readOnly()"
              [attr.aria-label]="'modules.form.parameters.typeAria' | translate"
              (change)="retypeParameter(i, $any($event.target).value)"
            >
              @for (type of parameterTypes; track type) {
                <option [value]="type">{{ 'modules.form.parameters.type.' + type | translate }}</option>
              }
            </select>
          </span>
          <span class="pc-col pc-col--required" role="cell">
            <button
              type="button"
              class="pc-toggle"
              [class.pc-toggle--on]="param.required"
              role="switch"
              [attr.aria-checked]="param.required"
              [attr.aria-label]="'modules.form.parameters.requiredToggleAria' | translate"
              [disabled]="readOnly()"
              (click)="toggleParameterRequired(i)"
            >
              {{
                (param.required ? 'modules.form.parameters.required' : 'modules.form.parameters.optional') | translate
              }}
            </button>
          </span>
          <span class="pc-col pc-col--default" role="cell">
            <input
              class="pc-input pc-input--default"
              [value]="param.defaultValue ?? ''"
              [disabled]="readOnly() || param.type === 'SECRET'"
              [attr.aria-label]="'modules.form.parameters.defaultAria' | translate"
              [placeholder]="'modules.form.parameters.defaultPlaceholder' | translate"
              (input)="setParameterDefault(i, $any($event.target).value)"
            />
          </span>
          <span class="pc-col pc-col--action" role="cell">
            @if (!readOnly()) {
              <button
                type="button"
                class="pc-icon-btn"
                [attr.title]="'modules.form.parameters.removeTitle' | translate"
                [attr.aria-label]="'modules.form.parameters.remove' | translate"
                (click)="removeParameter(i)"
              >
                <mat-icon aria-hidden="true">delete_outline</mat-icon>
              </button>
            }
          </span>
        </div>
      }

      @if (!readOnly()) {
        <button type="button" class="pc-add-row" (click)="addParameter()">
          {{ 'modules.form.parameters.add' | translate }}
        </button>
      }
    </div>
    @if (hasInvalidParameters()) {
      <p class="pc-hint pc-hint--error" role="alert">
        {{ 'modules.form.parameters.invalidHint' | translate }}
      </p>
    }

    <div class="pc-section-label">{{ 'modules.form.returns.label' | translate }}</div>
    <div class="pc-pills">
      @for (ret of returns(); track trackRow($index, ret); let i = $index) {
        <span class="pc-pill" [class.pc-pill--invalid]="isNameInvalid(ret.name, duplicateReturnNames())">
          <span class="pc-pill__glyph" aria-hidden="true">↩</span>
          <input
            class="pc-input pc-input--pill"
            [value]="ret.name"
            [disabled]="readOnly()"
            [attr.aria-invalid]="isNameInvalid(ret.name, duplicateReturnNames())"
            [attr.aria-label]="'modules.form.returns.nameAria' | translate"
            [placeholder]="'modules.form.returns.namePlaceholder' | translate"
            (input)="renameReturn(i, $any($event.target).value)"
          />
          <span class="pc-pill__sep" aria-hidden="true">:</span>
          <select
            class="pc-select pc-select--pill"
            [value]="ret.type"
            [disabled]="readOnly()"
            [attr.aria-label]="'modules.form.returns.typeAria' | translate"
            (change)="retypeReturn(i, $any($event.target).value)"
          >
            @for (type of returnTypes; track type) {
              <option [value]="type">{{ 'modules.form.parameters.type.' + type | translate }}</option>
            }
          </select>
          @if (!readOnly()) {
            <button
              type="button"
              class="pc-pill__remove"
              [attr.title]="'modules.form.returns.remove' | translate"
              [attr.aria-label]="'modules.form.returns.remove' | translate"
              (click)="removeReturn(i)"
            >
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          }
        </span>
      }
      @if (!readOnly()) {
        <button type="button" class="pc-add-pill" (click)="addReturn()">
          {{ 'modules.form.returns.add' | translate }}
        </button>
      }
    </div>
    @if (hasInvalidReturns()) {
      <p class="pc-hint pc-hint--error" role="alert">
        {{ 'modules.form.returns.invalidHint' | translate }}
      </p>
    }
  </div>
</div>
